import type { BondBidResult } from "@/lib/brokerBonds";
import { bidGapPercent, bidOutcomeLabel } from "@/lib/bidResult";
import { formatBondCurrency, formatBondDate } from "@/lib/bondValueDisplay";
import { bondsRequestedLabel } from "@/lib/wonBid";

// BRK-S183 — a RECORDED bid result, read-only. Moved out of the wave-1 panel so
// the bid result screen renders it exactly as BRK-S141 did, plus bid result
// v2's won figures when a result carries them (pinned at BRK-S280; null on
// every v1 result, so the record reads as it always has).

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <dt className="text-xs uppercase tracking-wide text-brk-dim">{label}</dt>
      <dd className="break-words font-medium text-brk-text">{value}</dd>
    </div>
  );
}

export function BidResultRecord({
  result,
  ourBid,
}: {
  result: BondBidResult;
  /** The server's echoed `ourBidAmount`, else the order's own bag. */
  ourBid: number | null;
}) {
  const next = result.nextLowestBidAmount;
  const bonds = bondsRequestedLabel(result.bondsRequested);
  return (
    <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <Figure label="Outcome" value={bidOutcomeLabel(result.outcome)} />
      {typeof result.finalContractPrice === "number" && (
        <Figure
          label="Final contract price"
          value={formatBondCurrency(result.finalContractPrice)}
        />
      )}
      {next !== null && (
        <Figure label="Next lowest bid" value={formatBondCurrency(next)} />
      )}
      {typeof result.nextClosestBidAmount === "number" && (
        <Figure
          label="Next-closest bid"
          value={formatBondCurrency(result.nextClosestBidAmount)}
        />
      )}
      {typeof result.secondClosestBidAmount === "number" && (
        <Figure
          label="Second-closest bid"
          value={formatBondCurrency(result.secondClosestBidAmount)}
        />
      )}
      {bonds && <Figure label="Bonds requested" value={bonds} />}
      {ourBid !== null && (
        <Figure label="Your bid" value={formatBondCurrency(ourBid)} />
      )}
      {result.outcome === "lost" &&
        ourBid !== null &&
        next !== null &&
        next > 0 && (
          <Figure
            label="Gap"
            value={`${String(bidGapPercent(ourBid, next))}% below`}
          />
        )}
      <Figure label="Recorded" value={formatBondDate(result.recordedAt)} />
      {result.explanation && (
        <div className="sm:col-span-2">
          <Figure label="Explanation" value={result.explanation} />
        </div>
      )}
    </dl>
  );
}
